// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { Check, Circle } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { usePostDealInvite } from "@/hooks/useInvitations";
import { ModalCloseButton, ModalOverlay } from "@/components/ui/dialog";
import { DEAL_SIDE_LABELS } from "@/constant/deals.constant";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/forms/FormControls";
import { delegatedInviteSchema } from "@/schemas/deals";

export function EmptyCard({ icon: Icon, text, testId }) {
  return (
    <div className="bg-white border border-dash-border rounded-2xl p-5 flex items-center gap-3" data-testid={testId}>
      <Icon className="w-4 h-4 flex-shrink-0 text-dash-faint" strokeWidth={1.5} />
      <span className="text-sm text-dash-faint">{text}</span>
    </div>
  );
}

export function ItemRow({ complete, title, trailing, onClick, busy, testId }) {
  const Wrapper = onClick ? Button : "div";
  return (
    <Wrapper
      variant={onClick ? "seamless" : undefined}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      loading={onClick ? busy : undefined}
      className="w-full flex items-center justify-between gap-3 bg-dash-page rounded-xl px-3 py-3 text-left"
      data-testid={testId}
    >
      <span className="flex items-center gap-2.5 min-w-0">
        {complete ? (
          <Check className="w-4 h-4 flex-shrink-0 text-dash-greenMid" strokeWidth={2} />
        ) : (
          <Circle className="w-4 h-4 flex-shrink-0 text-dash-faint" strokeWidth={1.5} />
        )}
        <span className={`text-sm font-medium truncate ${complete ? "text-dash-ink line-through" : "text-dash-ink"}`}>{title}</span>
      </span>
      {trailing}
    </Wrapper>
  );
}

export function DelegatedInviteModal({ dealId, side, onClose, onSent }) {
  const { mutate: postDealInvite, isPending: busy } = usePostDealInvite();
  const form = useForm({ resolver: zodResolver(delegatedInviteSchema), defaultValues: { email: "", name: "" } });

  const submit = (values) => {
    postDealInvite(
      {
        email: values.email,
        deal_id: dealId,
        participant_role: "client",
        name: values.name || undefined,
        origin_url: window.location.origin,
      },
      { onSuccess: () => onSent?.() }
    );
  };

  return (
    <ModalOverlay testId="delegated-invite-modal">
      <div className="bg-white w-full max-w-md border border-edge">
        <div className="p-6 border-b border-edge flex items-center justify-between">
          <div>
            <div className="overline">Invite your client</div>
            <div className="font-display text-xl tracking-tight mt-1">Add them to this room only.</div>
          </div>
          <ModalCloseButton onClick={onClose} data-testid="delegated-invite-close" />
        </div>
        <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} className="p-6 flex flex-col gap-4" noValidate>
          <p className="text-xs text-stone leading-relaxed">
            Scoped to this deal only. They&apos;ll see the {DEAL_SIDE_LABELS[side] || "buyer"} checklist. They never join the host&apos;s workspace.
          </p>
          <FormInput name="email" label="Client email" type="email" placeholder="name@example.com" data-testid="delegated-invite-email" required />
          <FormInput name="name" label="Name (optional)" placeholder="Enter their name" data-testid="delegated-invite-name" />
          <Button type="submit" variant="dashboardPrimary" loading={busy} className="w-full" data-testid="delegated-invite-submit">
            Send invite
          </Button>
        </form>
        </Form>
      </div>
    </ModalOverlay>
  );
}
